import React from 'react';
import { useTranslation } from 'react-i18next';
import OpArticle, { ArticleSection } from '../components/optimus/OpArticle';

interface AIIntegrationGuidePageProps {
  darkMode: boolean;
}

const CODE_SETUP = `// api/ai.js (server side only)
import OpenAI from 'openai';

const client = new OpenAI({
  apiKey: process.env.OPENAI_API_KEY,
});

const config = {
  maxTokens: 150,
  temperature: 0.7,
  timeout: 30000,
  retries: 3,
};`;

const CODE_BACKOFF = `// Exponential backoff for rate limits
async function withRetry(fn, retries = 3) {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (err.status !== 429 || i === retries - 1) throw err;
      await new Promise((r) => setTimeout(r, 2 ** i * 1000));
    }
  }
}`;

const AIIntegrationGuidePage: React.FC<AIIntegrationGuidePageProps> = ({ darkMode }) => {
  const { t } = useTranslation();
  const b = 'op.blog.ai';

  const sections: ArticleSection[] = [
    {
      title: t(`${b}.s1t`),
      paragraphs: [t(`${b}.s1p1`)],
      code: { filename: 'api/ai.js', content: CODE_SETUP },
    },
    { title: t(`${b}.s2t`), paragraphs: [t(`${b}.s2p1`)] },
    {
      title: t(`${b}.s3t`),
      paragraphs: [t(`${b}.s3p1`)],
      code: { filename: 'api/retry.js', content: CODE_BACKOFF },
    },
    { title: t(`${b}.s4t`), paragraphs: [t(`${b}.s4p1`)] },
    { title: t(`${b}.s5t`), paragraphs: [t(`${b}.s5p1`)] },
  ];

  const takeaways = [1, 2, 3, 4].map((n) => ({
    title: t(`${b}.k${n}t`),
    description: t(`${b}.k${n}d`),
  }));

  return (
    <OpArticle
      darkMode={darkMode}
      slug="ai-integration-guide"
      category={t(`${b}.category`)}
      title={t('blogPosts.aiIntegrationGuide.title')}
      description={t('blogPosts.aiIntegrationGuide.description')}
      date="2025-06-02"
      readingTime={7}
      tags={['AI', 'OpenAI', 'Streaming', 'API Design', 'Production']}
      seoKeywords="ai integration guide, openai api integration, ai chat production, streaming responses, ai error handling"
      sections={sections}
      takeaways={takeaways}
    />
  );
};

export default AIIntegrationGuidePage;
